import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log actual application errors, not extension-related ones
    if (!error.message?.includes('message port closed') && 
        !error.message?.includes('runtime.lastError')) {
      console.error('Uncaught error:', error, errorInfo);
    }
  }

  public render() {
    if (this.state.hasError) {
      const showDevDetails =
        import.meta.env.DEV ||
        (typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('debug') === '1');

      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="text-center max-w-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">Please refresh the page and try again.</p>
            {showDevDetails && this.state.error ? (
              <pre
                className="mb-4 max-h-48 overflow-auto rounded-lg border border-red-200 bg-red-50 p-3 text-left text-xs text-red-800 whitespace-pre-wrap"
              >
                {this.state.error.message}
                {'\n\n'}
                {this.state.error.stack}
              </pre>
            ) : null}
            <button
              onClick={() => this.setState({ hasError: false, error: undefined })}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
